/**
 * AdminDialogShell — an accessible modal shell (focus trap + Esc + scrim +
 * focus-restore) for the consult reschedule/cancel dialogs. NO window.confirm.
 * Modelled on the portal's MoveConfirmDialog. `busy` locks Esc / scrim-close
 * while an async action is in flight so the operator can't dismiss mid-call.
 */
import React, { useEffect, useRef } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function AdminDialogShell({
  role = 'dialog',
  labelledById,
  describedById,
  onClose,
  busy = false,
  children,
}: {
  role?: 'dialog' | 'alertdialog';
  labelledById: string;
  describedById?: string;
  onClose: () => void;
  busy?: boolean;
  children: React.ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const busyRef = useRef(busy);
  busyRef.current = busy;

  // Esc closes (unless an action is in flight).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busyRef.current) {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Focus in on open (first focusable in the panel), lock background scroll,
  // restore focus to the opener on close.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const node = panelRef.current;
    const first = node?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? node)?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
      if (opener && opener.isConnected) opener.focus?.();
    };
  }, []);

  function trapTab(e: React.KeyboardEvent) {
    if (e.key !== 'Tab') return;
    const node = panelRef.current;
    if (!node) return;
    const items = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null,
    );
    if (items.length === 0) {
      e.preventDefault();
      node.focus();
      return;
    }
    const firstEl = items[0];
    const lastEl = items[items.length - 1];
    const active = document.activeElement;
    if (e.shiftKey) {
      if (active === firstEl || active === node) {
        e.preventDefault();
        lastEl.focus();
      }
    } else if (active === lastEl) {
      e.preventDefault();
      firstEl.focus();
    }
  }

  return (
    <div
      className="cad-overlay"
      role={role}
      aria-modal="true"
      aria-labelledby={labelledById}
      aria-describedby={describedById}
      onKeyDown={trapTab}
    >
      <div
        className="cad-scrim"
        onClick={() => {
          if (!busy) onClose();
        }}
      />
      <div className="cad-panel" ref={panelRef} tabIndex={-1}>
        {children}
      </div>
    </div>
  );
}
